import { getSupabase as getSupabaseNullable } from "../../lib/supabase";
import type { ApiKey, ApiKeyBase } from "../../types";
import type { IOperationResult, IGetAllOptions } from "../types";
import { fromDb, toDb } from "./baseService";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

/**
 * Generate a random API key with a "vtk_" prefix.
 * Returns both the raw key (shown once) and a SHA-256 hash (stored).
 */
async function generateApiKey(): Promise<{ rawKey: string; keyHash: string; keyPrefix: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const rawKey = "vtk_" + Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
  const keyPrefix = rawKey.substring(0, 12);

  // SHA-256 hash for storage
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(rawKey));
  const keyHash = Array.from(new Uint8Array(hashBuffer), (b) => b.toString(16).padStart(2, "0")).join("");

  return { rawKey, keyHash, keyPrefix };
}

export class ApiKeyService {
  static async getAll(options?: IGetAllOptions): Promise<IOperationResult<ApiKey[]>> {
    try {
      const supabase = requireSupabase();
      let query = supabase.from("api_keys").select("*", { count: "exact" });

      if (options?.search) {
        const term = `%${options.search}%`;
        query = query.or(`name.ilike.${term},key_prefix.ilike.${term}`);
      }

      if (options?.orderBy?.length) {
        for (const orderSpec of options.orderBy) {
          const [field, dir = "asc"] = orderSpec.split(" ");
          query = query.order(field.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`), {
            ascending: dir === "asc",
          });
        }
      }

      if (options?.skip != null && options?.top != null) {
        query = query.range(options.skip, options.skip + options.top - 1);
      } else if (options?.top != null) {
        query = query.limit(options.top);
      }

      const { data, error, count } = await query;
      if (error) return { success: false, error };

      const mapped = (data ?? []).map((row) => fromDb<ApiKey>(row as Record<string, unknown>));
      return { success: true, data: mapped, totalCount: count ?? undefined };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async get(id: string): Promise<IOperationResult<ApiKey>> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase.from("api_keys").select("*").eq("id", id).single();
      if (error) return { success: false, error };
      return { success: true, data: fromDb<ApiKey>(data as Record<string, unknown>) };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Create a new API key. Returns the raw key in `rawKey` (shown once to user).
   */
  static async create(
    record: Omit<ApiKeyBase, "keyHash" | "keyPrefix">
  ): Promise<IOperationResult<ApiKey & { rawKey: string }>> {
    try {
      const supabase = requireSupabase();
      const { rawKey, keyHash, keyPrefix } = await generateApiKey();

      const dbRecord = toDb({
        ...record,
        keyHash,
        keyPrefix,
      } as Record<string, unknown>);

      // Set tenant_id from JWT
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const payload = JSON.parse(atob(session.access_token.split(".")[1]));
        dbRecord.tenant_id = payload.tenant_id;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        dbRecord.created_by = user.id;
      }

      const { data, error } = await supabase
        .from("api_keys")
        .insert(dbRecord)
        .select()
        .single();

      if (error) return { success: false, error };
      return {
        success: true,
        data: { ...fromDb<ApiKey>(data as Record<string, unknown>), rawKey },
      };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async update(id: string, fields: Partial<ApiKeyBase>): Promise<IOperationResult<ApiKey>> {
    try {
      const supabase = requireSupabase();
      const dbFields = toDb(fields as Record<string, unknown>);

      const { data, error } = await supabase
        .from("api_keys")
        .update(dbFields)
        .eq("id", id)
        .select()
        .single();

      if (error) return { success: false, error };
      return { success: true, data: fromDb<ApiKey>(data as Record<string, unknown>) };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async delete(id: string): Promise<IOperationResult<void>> {
    try {
      const supabase = requireSupabase();
      const { error } = await supabase.from("api_keys").delete().eq("id", id);
      if (error) return { success: false, error };
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Rotate: deactivate old key, create a new one with same config.
   */
  static async rotate(id: string): Promise<IOperationResult<ApiKey & { rawKey: string }>> {
    try {
      const existing = await ApiKeyService.get(id);
      if (!existing.success || !existing.data) return { success: false, error: "Key not found" };

      // Deactivate old key
      await ApiKeyService.update(id, { isActive: false });

      // Create new key with same config
      return ApiKeyService.create({
        name: existing.data.name,
        scopes: existing.data.scopes,
        rateLimit: existing.data.rateLimit,
        tenantId: existing.data.tenantId,
        isActive: true,
        expiresAt: existing.data.expiresAt,
      });
    } catch (error) {
      return { success: false, error };
    }
  }
}
